// Cover.tsx — Phase 11 Step 3 Block 2
// Wiederverwendbare Scrolly-Cover-Komponente.
//
// Verknüpft:
//   - PaletteKey (mock/scrollys.ts) → COVER_PALETTES (lib/tokens.ts)
//   - DecorationId (mock/scrollys.ts) → interne 9 Decoration-Primitives
//
// Quelle: handoff/phase-11/cover-variants.jsx
//   Die 9 Decorations heißen `bigNumber, chart, column, dots, grid,
//   headlines, mountain, phones, stripes` — das ist Block-1-Schema.
//
// Styling: Nur Tokens (VARIANT, FONT_DISPLAY, COVER_PALETTES). Keine
// hardcoded Farben außer semi-transparenten weißen Overlays, die hier
// Textur-Funktion haben und keine Brand-Farben sind.

import type { CSSProperties, ReactNode } from "react";
import {
  COVER_PALETTES,
  FONT_DISPLAY,
  FONT_KACHEL,
  FONT_UI,
  type CoverPalette,
} from "../../lib/tokens";
import { seededRandom } from "../../lib/math";
import type {
  DecorationId,
  PaletteKey,
  ScrollyCreatorRef,
} from "../../lib/mock/scrollys";

// ---- Props --------------------------------------------------------------

export type CoverProps = {
  /** Palette-Key aus Scrolly-Kategorie. Wird über COVER_PALETTES aufgelöst. */
  palette: PaletteKey;
  /** Eine der 9 Decorations. */
  decoration: DecorationId;
  /** Fallback-Kategorie-Label, im Overlay oben dargestellt. */
  category: string;
  /** Scrolly-Titel, mehrzeilig geclampt. */
  title: string;
  /** Creator-Referenz (Handle/Name/Avatar-Farbe) — aktuell ungenutzt,
   *  wird in Block 3 für die Card-Meta-Leiste relevant. */
  creator?: ScrollyCreatorRef;
  /** Aspect-Ratio — Scrolly-Eigenschaft, deshalb Prop.
   *  Default `3/4`. Optional `9/16` für Phone-Story-Cover. */
  aspect?: "3/4" | "9/16";
  /** Nur bei `decoration === "bigNumber"`. Redaktionelle Kurz-Zahl. */
  bigNumberValue?: string;
};

// ---- Cover --------------------------------------------------------------

export function Cover({
  palette,
  decoration,
  category,
  title,
  aspect = "3/4",
  bigNumberValue,
}: CoverProps) {
  const p = COVER_PALETTES[palette] ?? COVER_PALETTES.brand;
  return (
    <CoverFrame palette={p} category={category} title={title} aspect={aspect}>
      <DecorationLayer
        decoration={decoration}
        accent={p.accent}
        bigNumberValue={bigNumberValue}
      />
    </CoverFrame>
  );
}

// ---- Frame --------------------------------------------------------------

function CoverFrame({
  palette,
  category,
  title,
  aspect,
  children,
}: {
  palette: CoverPalette;
  category: string;
  title: string;
  aspect: "3/4" | "9/16";
  children: ReactNode;
}) {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        aspectRatio: aspect === "9/16" ? "9 / 16" : "3 / 4",
        background: palette.bg,
        position: "relative",
        overflow: "hidden",
        borderRadius: 14,
      }}
    >
      {children}

      {/* Legibility-Gradient für Titel am unteren Rand */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to bottom, transparent 30%, rgba(0,0,0,0.0) 45%, rgba(0,0,0,0.75) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* Title-Overlay */}
      <div
        style={{
          position: "absolute",
          left: 14,
          right: 14,
          bottom: 12,
          color: "#fff",
        }}
      >
        <div
          style={{
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: 0.9,
            textTransform: "uppercase",
            opacity: 0.9,
            marginBottom: 6,
            color: palette.accent,
            fontFamily: FONT_UI,
          }}
        >
          {category}
        </div>
        <div
          style={{
            fontFamily: FONT_KACHEL,
            fontSize: 23,
            fontWeight: 600,
            letterSpacing: 0.2,
            lineHeight: 1.05,
            textShadow: "0 1px 8px rgba(0,0,0,0.35)",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {title}
        </div>
      </div>
    </div>
  );
}

// ---- Decoration-Switch --------------------------------------------------

function DecorationLayer({
  decoration,
  accent,
  bigNumberValue,
}: {
  decoration: DecorationId;
  accent: string;
  bigNumberValue?: string;
}) {
  switch (decoration) {
    case "bigNumber":
      return <DecBigNumber value={bigNumberValue ?? ""} accent={accent} />;
    case "chart":
      return <DecChart accent={accent} />;
    case "column":
      return <DecColumn accent={accent} />;
    case "dots":
      return <DecDots accent={accent} />;
    case "grid":
      return <DecGrid accent={accent} />;
    case "headlines":
      return <DecHeadlines accent={accent} />;
    case "mountain":
      return <DecMountain />;
    case "phones":
      return <DecPhones accent={accent} />;
    case "stripes":
      return <DecStripes accent={accent} />;
  }
}

// ---- Decoration-Primitives ----------------------------------------------
// 1:1 aus handoff/phase-11/cover-variants.jsx portiert. Abweichungen:
// - DecDots: deterministischer Seed statt `(i*37)%100`-Pseudo-Random.
// - DecPhones: JSDoc-Kommentar zur absichtlichen Unregelmäßigkeit.

const SVG_FULL: CSSProperties = { position: "absolute", inset: 0 };

function DecChart({ accent }: { accent: string }) {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 100 140"
      preserveAspectRatio="none"
      style={SVG_FULL}
      aria-hidden="true"
    >
      <path
        d="M0 90 Q20 82 30 70 T55 52 T85 18 L100 10"
        stroke={accent}
        strokeWidth="1.5"
        fill="none"
        opacity="0.85"
      />
      <path
        d="M0 90 Q20 82 30 70 T55 52 T85 18 L100 10 L100 140 L0 140 Z"
        fill={accent}
        opacity="0.18"
      />
      {[20, 45, 65, 85, 95].map((x, i) => {
        const y = [82, 65, 50, 30, 15][i];
        return (
          <circle
            key={i}
            cx={x}
            cy={y}
            r="1.4"
            fill="#fff"
            stroke={accent}
            strokeWidth="0.5"
          />
        );
      })}
      {[25, 50, 75, 100].map((y, i) => (
        <line
          key={i}
          x1="0"
          x2="100"
          y1={y}
          y2={y}
          stroke="#fff"
          strokeWidth="0.2"
          opacity="0.1"
        />
      ))}
    </svg>
  );
}

function DecHeadlines({ accent }: { accent: string }) {
  const rows = [
    { y: 18, w: 78, h: 2.8, accent: false },
    { y: 26, w: 56, h: 1.6, accent: false },
    { y: 32, w: 66, h: 1.6, accent: false },
    { y: 42, w: 44, h: 2.4, accent: true },
    { y: 50, w: 62, h: 1.4, accent: false },
    { y: 56, w: 72, h: 1.4, accent: false },
    { y: 62, w: 38, h: 1.4, accent: false },
    { y: 72, w: 56, h: 1.4, accent: false },
    { y: 78, w: 68, h: 1.4, accent: false },
    { y: 84, w: 42, h: 1.4, accent: false },
  ];
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 100 140"
      preserveAspectRatio="none"
      style={{ ...SVG_FULL, opacity: 0.92 }}
      aria-hidden="true"
    >
      {rows.map((r, i) => (
        <rect
          key={i}
          x="11"
          y={r.y}
          width={r.w}
          height={r.h}
          fill={r.accent ? accent : "#fff"}
          opacity={r.accent ? 0.85 : 0.28}
          rx="0.6"
        />
      ))}
      <line
        x1="55"
        y1="14"
        x2="55"
        y2="92"
        stroke="#fff"
        strokeWidth="0.25"
        opacity="0.18"
      />
    </svg>
  );
}

/**
 * DecDots — sternenartiger Punkteteppich.
 *
 * Positionen sind deterministisch über `seededRandom` (Mulberry32) gesetzt,
 * damit Screenshot-Regressionstests stabile Frames produzieren. Das alte
 * `(i*37) % 100`-Schema lieferte zwar fix-reproduzierbare Werte, fühlte sich
 * aber durch die simple Modulo-Struktur visuell geradlinig an — der
 * seed-basierte Ansatz streut gleichmäßiger.
 */
function DecDots({ accent }: { accent: string }) {
  const dots = Array.from({ length: 46 }, (_, i) => ({
    x: seededRandom(i * 2 + 1) * 100,
    y: seededRandom(i * 2 + 2) * 130,
    r: 0.7 + seededRandom(i * 2 + 3) * 2.2,
    o: 0.3 + seededRandom(i * 2 + 4) * 0.55,
  }));
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 100 140"
      preserveAspectRatio="xMidYMid slice"
      style={SVG_FULL}
      aria-hidden="true"
    >
      {dots.map((d, i) => (
        <circle key={i} cx={d.x} cy={d.y} r={d.r} fill={accent} opacity={d.o} />
      ))}
    </svg>
  );
}

/**
 * DecPhones — gestaffelter Smartphone-Stack.
 *
 * Der Stack ist **bewusst leicht unregelmäßig** gebaut:
 *   - `translateY(-${i * 2}%)` staffelt die Phones vertikal nach oben,
 *   - `rotate(${(i - 2) * 3}deg)` fächert sie um das mittlere Gerät.
 * Die mittlere Zelle ist das visuelle Zentrum (Accent-Farbe, höhere Opazität).
 * Bitte nicht „geradeziehen“ — die Unruhe ist Teil des Design-Signals.
 */
function DecPhones({ accent }: { accent: string }) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        paddingBottom: "30%",
      }}
      aria-hidden="true"
    >
      <div style={{ display: "flex", alignItems: "flex-end", gap: 3 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            style={{
              width: 24,
              aspectRatio: "9 / 19",
              background: i === 2 ? accent : "rgba(0,0,0,0.4)",
              border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: "14%",
              transform: `translateY(-${i * 2}%) rotate(${(i - 2) * 3}deg)`,
              opacity: i === 2 ? 0.9 : 0.7,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function DecColumn({ accent }: { accent: string }) {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 100 140"
      preserveAspectRatio="none"
      style={SVG_FULL}
      aria-hidden="true"
    >
      {/* Kapitell */}
      <rect x="32" y="18" width="36" height="4" fill={accent} opacity="0.7" />
      <rect x="30" y="22" width="40" height="3" fill={accent} opacity="0.5" />
      <rect x="28" y="25" width="44" height="2" fill={accent} opacity="0.4" />
      {/* Schaft */}
      <rect x="34" y="27" width="32" height="86" fill={accent} opacity="0.15" />
      {/* Kanneluren */}
      {[38, 42, 46, 50, 54, 58, 62].map((x, i) => (
        <line
          key={i}
          x1={x}
          y1="28"
          x2={x}
          y2="112"
          stroke={accent}
          strokeWidth="0.3"
          opacity="0.55"
        />
      ))}
      {/* Sockel */}
      <rect x="28" y="113" width="44" height="2" fill={accent} opacity="0.4" />
      <rect x="30" y="115" width="40" height="3" fill={accent} opacity="0.5" />
      <rect x="32" y="118" width="36" height="4" fill={accent} opacity="0.7" />
      {/* Ground */}
      <rect x="20" y="122" width="60" height="1.2" fill={accent} opacity="0.3" />
    </svg>
  );
}

function DecMountain() {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 100 140"
      preserveAspectRatio="xMidYMid slice"
      style={SVG_FULL}
      aria-hidden="true"
    >
      <circle cx="75" cy="22" r="8" fill="#fff" opacity="0.85" />
      <path
        d="M0 95 L25 55 L40 75 L60 40 L80 68 L100 50 L100 140 L0 140 Z"
        fill="rgba(30,50,80,0.55)"
      />
      <path
        d="M0 110 L20 85 L45 100 L70 75 L100 95 L100 140 L0 140 Z"
        fill="rgba(10,30,55,0.7)"
      />
    </svg>
  );
}

/**
 * DecBigNumber — redaktionelle Kurz-Zahl als Cover-Decoration.
 *
 * TODO: fontSize responsive reduzieren wenn 9/16-Aspect produktiv.
 *   Aktuell fix 160px — passt gut für 3/4, wirkt in 9/16 durch die
 *   engere Breite gedrängt. Sobald Phone-Story-Cover live gehen,
 *   auf aspect-abhängiges Clamping umstellen (z.B. clamp via
 *   container queries oder Prop-Durchreichung aus CoverFrame).
 */
function DecBigNumber({ value, accent }: { value: string; accent: string }) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: FONT_DISPLAY,
        fontSize: 160,
        fontWeight: 500,
        letterSpacing: -6,
        color: accent,
        opacity: 0.95,
        lineHeight: 1,
        userSelect: "none",
      }}
      aria-hidden="true"
    >
      {value}
    </div>
  );
}

function DecGrid({ accent }: { accent: string }) {
  return (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 100 140"
      style={{ ...SVG_FULL, opacity: 0.36 }}
      aria-hidden="true"
    >
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <line
          key={`h${i}`}
          x1="0"
          x2="100"
          y1={i * 25}
          y2={i * 25}
          stroke={accent}
          strokeWidth="0.4"
        />
      ))}
      {[0, 1, 2, 3, 4].map((i) => (
        <line
          key={`v${i}`}
          x1={i * 25}
          x2={i * 25}
          y1="0"
          y2="140"
          stroke={accent}
          strokeWidth="0.4"
        />
      ))}
      {/* Schnittmuster-Markierungen */}
      {(
        [
          [25, 50],
          [75, 75],
          [50, 100],
        ] as const
      ).map(([x, y], i) => (
        <g key={`m${i}`}>
          <line
            x1={x - 3}
            y1={y}
            x2={x + 3}
            y2={y}
            stroke={accent}
            strokeWidth="0.8"
            opacity="1"
          />
          <line
            x1={x}
            y1={y - 3}
            x2={x}
            y2={y + 3}
            stroke={accent}
            strokeWidth="0.8"
            opacity="1"
          />
        </g>
      ))}
    </svg>
  );
}

function DecStripes({ accent }: { accent: string }) {
  return (
    <div
      style={{ position: "absolute", inset: 0, opacity: 0.32 }}
      aria-hidden="true"
    >
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            top: `${-5 + i * 18}%`,
            left: "-10%",
            right: "-10%",
            height: "3%",
            background: accent,
            transform: `rotate(-8deg) translateY(${i * 6}px)`,
          }}
        />
      ))}
    </div>
  );
}

// ---- Brand-Varianten ----------------------------------------------------

export type CoverBrandWithBrandingProps = {
  category: string;
  title: string;
  /** Temporärer Fallback-Buchstabe/Initialen im Logo-Slot. */
  orgInitials: string;
  /** Gradient-Endpunkte + Accent der Org. Platzhalter bis `useBranding()` kommt. */
  orgPrimary: string;
  orgSecondary: string;
  orgAccent: string;
  aspect?: "3/4" | "9/16";
};

/**
 * CoverBrandWithBranding — Scrolly einer Creator-Organisation **mit**
 * hinterlegtem Org-Branding (Primary/Secondary/Accent + Logo).
 *
 * Rendert Org-Gradient + dezente Accent-Linien + Logo-Slot oben links.
 * In Block 2 reicht ein Platzhalter-Logo (Initialen in gerundetem Kasten).
 *
 * TODO(phase-10.5a): echtes Org-Logo aus `useBranding()` laden,
 *   sobald der Hook auf `main` liegt. Dann Signatur auf
 *   `useBranding().orgLogoUrl | orgInitialsFallback` umstellen.
 */
export function CoverBrandWithBranding({
  category,
  title,
  orgInitials,
  orgPrimary,
  orgSecondary,
  orgAccent,
  aspect = "3/4",
}: CoverBrandWithBrandingProps) {
  const palette: CoverPalette = {
    bg: `linear-gradient(160deg, ${orgSecondary} 0%, ${orgPrimary} 100%)`,
    accent: orgAccent,
  };
  return (
    <CoverFrame palette={palette} category={category} title={title} aspect={aspect}>
      {/* Org-Logo-Slot — Platzhalter mit Initialen. */}
      {/* TODO(phase-10.5a): durch <OrgLogo /> aus useBranding() ersetzen. */}
      <div
        style={{
          position: "absolute",
          top: 14,
          left: 14,
          width: 32,
          height: 32,
          borderRadius: 8,
          background: "rgba(255,255,255,0.18)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONT_DISPLAY,
          fontSize: 20,
          fontWeight: 500,
          color: "#fff",
          letterSpacing: -0.5,
        }}
      >
        {orgInitials}
      </div>
      {/* Dezentes Branding-Pattern */}
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 100 140"
        preserveAspectRatio="none"
        style={{ ...SVG_FULL, opacity: 0.18 }}
        aria-hidden="true"
      >
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <line
            key={i}
            x1={i * 16 - 20}
            y1="0"
            x2={i * 16 + 30}
            y2="140"
            stroke={orgAccent}
            strokeWidth="0.8"
          />
        ))}
      </svg>
    </CoverFrame>
  );
}

export type CoverBrandFallbackProps = {
  /** Kategorie-Palette des Scrollys — die Kategorie gewinnt gegenüber einer
   *  generischen "Brand"-Fallback-Farbe (Session C Q&A F3). */
  palette: PaletteKey;
  category: string;
  title: string;
  aspect?: "3/4" | "9/16";
};

/**
 * CoverBrandFallback — Scrolly einer Creator-Org **ohne** hinterlegtes
 * Branding. Statt eines generischen Dunkel-Fallbacks rendert die Komponente
 * die **Kategorie-Palette** des Scrollys plus ein `DecStripes`-Overlay als
 * wiedererkennbares Textur-Pattern.
 *
 * Beispiel: IKEA-Scrolly in Kategorie `brand` nutzt `COVER_PALETTES.brand`
 * (Dunkel-Magenta) + Stripes — kein Sonderfall, sondern Kategorie + Pattern.
 */
export function CoverBrandFallback({
  palette,
  category,
  title,
  aspect = "3/4",
}: CoverBrandFallbackProps) {
  const p = COVER_PALETTES[palette] ?? COVER_PALETTES.brand;
  return (
    <CoverFrame palette={p} category={category} title={title} aspect={aspect}>
      <DecStripes accent={p.accent} />
    </CoverFrame>
  );
}
